import { useCallback, useContext, useRef, useState } from "react";
import "./plugins";
import ActivityTableProvider from "../ActivityTableProvider";
import { ActivityListManager } from "./ActivityListManager";
import { ActivityPendingSection } from "./ActivityPendingManager";
import CompanyService from "../../../../services/CompanyService";
import { TranslationService } from "../../../../services/TranslationService";
import { Client } from "../../entities/ClientGetResponse";
import { ModalTypeDefault } from "../../ClientDetail";
import TableContext from "../../../task/TableContext";
import ClientDetailContext from "../../ClientDetailContext";
import NewComment from "../components/NewComment";
import NewTaskForm from "../../../task/components/NewTaskForm";
import { SMSCompose } from "../../components/SMSCompose";
import { EmailCompose } from "../../components/EmailCompose";
import NewScheduledTaskForm from "../../../task/components/scheduled/add/NewScheduledTaskForm";
import FloatingPanelService from "../../../shared/FloatingPanel";
import { openWhatsappPanel } from "../../../../components/whatsapp/utils/openWhatsappPanel";
import MenuButton from "../../../shared/components/MenuButton";
import AdvancedFilters, {
  AdvancedFiltersButton,
  TYPE_REPORT
} from "../../../shared/components/AdvancedFilters";
import Dropdown from "../../../shared/components/Dropdown";
import { FixToTop } from "../../../shared/FixToTop";
import { FilterCollection } from "../../../../services/FilterService";
import { addIf } from "../../../../utils/Utils";
import { ActivityViewProvider, useActivityView } from "./ActivityViewContext";

interface ActivityHistoryManagerProps {
  personid: string;
  person?: Client;
  emailAddress: string;
  groupid?: number;
  setReloads?: { activity: () => void };
  openModalDefault?: ModalTypeDefault | string;
  reload: () => void;
}

type ActivityButton = {
  selected: number;
  text: string;
  action: () => void | Promise<void>;
};

const ActivityHistoryManagerInner = ({
  personid,
  person,
  emailAddress,
  groupid,
  setReloads,
  reloadClientDetail
}: Omit<ActivityHistoryManagerProps, "reload" | "openModalDefault"> & {
  reloadClientDetail: () => void;
}) => {
  const { applySearch, reload: reloadTable } = useContext(TableContext);
  const { translate } = TranslationService;
  const clientDetailContextValues = useContext(ClientDetailContext);
  const [selected, setSelected] = useState(0);
  const { view, setView } = useActivityView();

  const handleSetView = (newView: "detallada" | "compacta") => {
    const scrollY = window.scrollY;
    setView(newView);
    requestAnimationFrame(() => window.scrollTo(0, scrollY));
  };

  const pendingReloadRef = useRef<() => void>();

  const reloadHistory = useCallback(
    () => (reloadTable as (skipReloading: boolean) => void)(true),
    [reloadTable]
  );

  const reload = useCallback(
    (action?: (val?: unknown) => void) => {
      reloadHistory();
      pendingReloadRef.current?.();
      action?.(undefined);
    },
    [reloadHistory]
  );

  if (setReloads) {
    setReloads.activity = reload;
  }

  const openNewCommentModal = () => {
    FloatingPanelService.showPanel({
      title: translate.NewComment,
      children: (
        <NewComment
          clientid={personid}
          reload={reload}
          clientDetailContext={clientDetailContextValues}
          groupId={groupid}
        />
      ),
      width: 650,
      height: 750,
      position: "bottom-right"
    });
  };

  const openNewTaskModal = () => {
    FloatingPanelService.showPanel({
      title: translate.NewTask,
      children: (
        <NewTaskForm
          personId={personid}
          onNewTask={() => reload()}
          personName={person?.Name ?? ""}
          groupId={groupid}
        />
      ),
      width: 629,
      height: 650
    });
  };

  const openNewSMSModal = () => {
    FloatingPanelService.showPanel({
      title: translate.NewSMS,
      children: (
        <SMSCompose person={person!} onSubmit={reload} groupId={groupid} />
      ),
      width: 800,
      height: 600
    });
  };

  const openWhatsappNew = async () => {
    await openWhatsappPanel({
      client: person,
      isNewConversation: true,
      onSubmit: reload
    });
  };

  const openNewEmailModal = useCallback(() => {
    FloatingPanelService.showPanel({
      title: translate.EmailToClient,
      children: (
        <EmailCompose
          personId={personid}
          defaultTo={emailAddress}
          clientDetailContext={clientDetailContextValues}
          onSubmit={reload}
          groupId={groupid}
        />
      ),
      width: 900,
      height: 669
    });
  }, [
    clientDetailContextValues,
    emailAddress,
    groupid,
    personid,
    reload,
    translate.EmailToClient
  ]);

  const openNewScheduledTaskModal = () => {
    FloatingPanelService.showPanel({
      title: translate.NewScheduledTask,
      children: (
        <NewScheduledTaskForm
          personId={personid}
          personName={person?.Name ?? ""}
          onNewTask={() => reload()}
        />
      ),
      width: 629,
      height: 650
    });
  };

  const showWhatsapp = CompanyService.canUseWhatsapp();
  const showSMS =
    CompanyService.canShowSMS() && CompanyService.canDo("newmail");

  const onTabClick = useCallback(
    (selection: number) => {
      setSelected(selection);
      if (selection < 7 || selection === 9) {
        applySearch(selection.toString());
      }
    },
    [applySearch]
  );

  const buttons: ActivityButton[] = [
    ...addIf(CompanyService.canDo("newmail"), {
      selected: 1,
      text: translate.NewEmail,
      action: openNewEmailModal
    }),
    ...addIf(CompanyService.canDo("newcomment"), {
      selected: 2,
      text: translate.NewComment,
      action: openNewCommentModal
    }),
    ...addIf(CompanyService.canDo("newtask"), {
      selected: 3,
      text: translate.NewTask,
      action: openNewTaskModal
    }),
    ...addIf(showSMS, {
      selected: 4,
      text: translate.NewSMS,
      action: openNewSMSModal
    }),
    ...addIf(showWhatsapp, {
      selected: 6,
      text: translate.NewWhatsApp,
      action: openWhatsappNew
    }),
    ...addIf(
      !!CompanyService.getSetting("enabletaskshceduler") &&
        CompanyService.canDo("newtask"),
      {
        selected: 7,
        text: translate.ScheduledTaskButton,
        action: openNewScheduledTaskModal
      }
    )
  ];

  const buttonsSelect = [
    { id: 0, text: translate.AllActivities },
    ...addIf(CompanyService.canDo("newmail"), { id: 1, text: translate.Email }),
    ...addIf(CompanyService.canDo("newcomment"), {
      id: 2,
      text: translate.Comments
    }),
    ...addIf(CompanyService.canDo("newtask"), { id: 3, text: translate.Tasks }),
    ...addIf(showSMS, { id: 4, text: translate.SMS }),
    ...addIf(showWhatsapp, { id: 6, text: translate.WhatsApp }),
    { id: 9, text: translate.Pinned }
  ];

  const activeButton = buttons.find((b) => b.selected === selected);

  return (
    <>
      <div>
        <h3 className="mb-0 client-detail-section-title">
          {translate.PendingTask}
        </h3>
        <hr className="mb-3" id="pending-activity-header-marker" />
        <span />
        <div className="historyManagement">
          <ActivityPendingSection
            personid={personid}
            groupid={groupid}
            selected={selected}
            person={person}
            setReloadPending={(fn) => (pendingReloadRef.current = fn)}
            onPendingChange={reloadHistory}
          />
        </div>
      </div>

      <div>
        <h3 className="mb-0 mt-4 client-detail-section-title">
          {translate.ActivityHistory}
        </h3>
        <hr className="mb-0" id="activity-header-marker" />
        <div className="historyManagement">
          <span />
          <FixToTop backgroundClass="bg-intiza-grey">
            <div className="historyManagementActions">
              <div className="genericHeader ps-0" id="activity-table-header">
                <div className="d-flex col gap-12 align-items-center justify-content-between">
                  <div className="d-flex gap-2">
                    {selected && activeButton ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={activeButton.action}
                      >
                        <i className="fa-light fa-plus me-2" />
                        {activeButton.text}
                      </button>
                    ) : buttons?.length ? (
                      <MenuButton
                        extraClassName="mb-0"
                        text={translate.NewActivity}
                        actions={buttons}
                        icon="fa-light fa-plus"
                        showTitle={true}
                        hideChevron
                      />
                    ) : (
                      <></>
                    )}
                    <AdvancedFiltersButton
                      savedFiltersId={TYPE_REPORT["Activity"]}
                    />
                    <Dropdown
                      onChange={(x) => onTabClick(x)}
                      items={buttonsSelect.map((x) => ({
                        text: x.text,
                        value: x.id
                      }))}
                    />
                  </div>
                  <div className="d-flex align-items-center gap-1 ms-1 activity-view-toggle">
                    <button
                      type="button"
                      title="Detallada"
                      className={`btn btn-sm btn-outline-primary${view === "detallada" ? " active" : ""}`}
                      onClick={() => handleSetView("detallada")}
                    >
                      <i className="fa-regular fa-table-list" />
                    </button>
                    <button
                      type="button"
                      title="Compacta"
                      className={`btn btn-sm btn-outline-primary${view === "compacta" ? " active" : ""}`}
                      onClick={() => handleSetView("compacta")}
                    >
                      <i className="fa-regular fa-list" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <AdvancedFilters page={FilterCollection.Activity} />
          </FixToTop>
          <div className="historyManagement">
            <ActivityListManager
              reload={reload}
              reloadClientDetail={reloadClientDetail}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export const ActivityHistoryManager = ({
  personid,
  person,
  emailAddress,
  groupid,
  setReloads,
  reload
}: ActivityHistoryManagerProps) => {
  return (
    <ActivityViewProvider>
      <div>
        <ActivityTableProvider personid={personid} groupid={groupid}>
          <ActivityHistoryManagerInner
            personid={personid}
            person={person}
            emailAddress={emailAddress}
            groupid={groupid}
            setReloads={setReloads}
            reloadClientDetail={reload}
          />
        </ActivityTableProvider>
      </div>
    </ActivityViewProvider>
  );
};
